import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Badge,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Fab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Switch,
  FormControlLabel,
  LinearProgress,
  Avatar,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  FilterList,
  Refresh,
  Visibility,
  Download,
  Print,
  ExpandMore,
  Inventory,
  LocalShipping,
  Warning,
  CheckCircle,
  Error,
  TrendingUp,
  TrendingDown,
  Business,
  Storage,
  Assessment,
  Settings,
} from '@mui/icons-material';
import { useRbac } from '../../context/RbacContext';
import { useImportMaterialsMutation } from '../../api/inventoryApi';

interface Material {
  id: string;
  name: string;
  description: string;
  type: string;
  unit: string;
  stockQuantity: number;
  reorderLevel: number;
  unitPrice: number;
  supplierId: string;
  supplierName: string;
  location: string;
  isActive: boolean;
  lastUpdated: string;
  minStock: number;
  maxStock: number;
}

interface Supplier {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  isActive: boolean;
  rating: number;
}

interface StockMovement {
  id: string;
  materialId: string;
  materialName: string;
  type: 'in' | 'out' | 'adjustment';
  quantity: number;
  reason: string;
  date: string;
  userId: string;
  userName: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`inventory-tabpanel-${index}`}
      aria-labelledby={`inventory-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const InventoryManagement: React.FC = () => {
  const { canManage } = useRbac();
  const [tabValue, setTabValue] = useState(0);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<Material | Supplier | null>(null);
  const [dialogType, setDialogType] = useState<'material' | 'supplier' | 'movement'>('material');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importMaterials] = useImportMaterialsMutation();

  // Form states
  const [materialForm, setMaterialForm] = useState({
    name: '',
    description: '',
    type: '',
    unit: '',
    stockQuantity: 0,
    reorderLevel: 0,
    unitPrice: 0,
    supplierId: '',
    location: '',
    minStock: 0,
    maxStock: 0,
  });

  const [supplierForm, setSupplierForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    rating: 0,
  });

  const [movementForm, setMovementForm] = useState({
    materialId: '',
    type: 'in' as 'in' | 'out' | 'adjustment',
    quantity: 0,
    reason: '',
  });

  // Mock data
  useEffect(() => {
    setMaterials([
      {
        id: '1',
        name: 'Cotton Fabric',
        description: 'High-quality cotton fabric for clothing',
        type: 'fabric',
        unit: 'meters',
        stockQuantity: 150,
        reorderLevel: 50,
        unitPrice: 25.50,
        supplierId: '1',
        supplierName: 'Textile Corp',
        location: 'Warehouse A',
        isActive: true,
        lastUpdated: '2024-01-15T10:30:00Z',
        minStock: 20,
        maxStock: 200,
      },
      {
        id: '2',
        name: 'Oak Wood',
        description: 'Premium oak wood for furniture',
        type: 'wood',
        unit: 'pieces',
        stockQuantity: 25,
        reorderLevel: 30,
        unitPrice: 150.00,
        supplierId: '2',
        supplierName: 'Wood Supply Co',
        location: 'Warehouse B',
        isActive: true,
        lastUpdated: '2024-01-15T09:15:00Z',
        minStock: 10,
        maxStock: 50,
      },
    ]);

    setSuppliers([
      {
        id: '1',
        name: 'Textile Corp',
        email: 'contact@textilecorp.com',
        phone: '+1-555-0123',
        address: '123 Textile St, Fabric City, FC 12345',
        isActive: true,
        rating: 4.5,
      },
      {
        id: '2',
        name: 'Wood Supply Co',
        email: 'info@woodsupply.com',
        phone: '+1-555-0456',
        address: '456 Wood Ave, Timber Town, TT 67890',
        isActive: true,
        rating: 4.2,
      },
    ]);

    setStockMovements([
      {
        id: '1',
        materialId: '1',
        materialName: 'Cotton Fabric',
        type: 'in',
        quantity: 50,
        reason: 'Restock from supplier',
        date: '2024-01-15T10:30:00Z',
        userId: 'user1',
        userName: 'John Doe',
      },
      {
        id: '2',
        materialId: '2',
        materialName: 'Oak Wood',
        type: 'out',
        quantity: 5,
        reason: 'Production order #123',
        date: '2024-01-15T09:15:00Z',
        userId: 'user2',
        userName: 'Jane Smith',
      },
    ]);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (type: 'material' | 'supplier' | 'movement', item?: any) => {
    setDialogType(type);
    setEditingItem(item || null);
    setOpenDialog(true);
    
    // Populate form with existing data if editing
    if (item) {
      if (type === 'material') {
        setMaterialForm({
          name: item.name || '',
          description: item.description || '',
          type: item.type || '',
          unit: item.unit || '',
          stockQuantity: item.stockQuantity || 0,
          reorderLevel: item.reorderLevel || 0,
          unitPrice: item.unitPrice || 0,
          supplierId: item.supplierId || '',
          location: item.location || '',
          minStock: item.minStock || 0,
          maxStock: item.maxStock || 0,
        });
      } else if (type === 'supplier') {
        setSupplierForm({
          name: item.name || '',
          email: item.email || '',
          phone: item.phone || '',
          address: item.address || '',
          rating: item.rating || 0,
        });
      }
    } else {
      // Reset forms for new items
      if (type === 'material') {
        setMaterialForm({
          name: '',
          description: '',
          type: '',
          unit: '',
          stockQuantity: 0,
          reorderLevel: 0,
          unitPrice: 0,
          supplierId: '',
          location: '',
          minStock: 0,
          maxStock: 0,
        });
      } else if (type === 'supplier') {
        setSupplierForm({
          name: '',
          email: '',
          phone: '',
          address: '',
          rating: 0,
        });
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (editingItem) {
        // Update existing item
        if (dialogType === 'material') {
          setMaterials(prev => prev.map(m =>
            m.id === editingItem.id
              ? {
                  ...m,
                  ...materialForm,
                  supplierName: suppliers.find(s => s.id === materialForm.supplierId)?.name || '',
                  lastUpdated: new Date().toISOString(),
                  minStock: materialForm.minStock || m.minStock || 0,
                  maxStock: materialForm.maxStock || m.maxStock || 100,
                }
              : m
          ));
        } else if (dialogType === 'supplier') {
          setSuppliers(prev => prev.map(s =>
            s.id === editingItem.id
              ? {
                  ...s,
                  ...supplierForm,
                  rating: supplierForm.rating ?? s.rating ?? 0,
                }
              : s
          ));
        }
      } else {
        // Create new item
        if (dialogType === 'material') {
          const newMaterial: Material = {
            id: Date.now().toString(),
            ...materialForm,
            supplierName: suppliers.find(s => s.id === materialForm.supplierId)?.name || '',
            isActive: true,
            lastUpdated: new Date().toISOString(),
            minStock: materialForm.minStock || 0,
            maxStock: materialForm.maxStock || 100,
          };
          setMaterials(prev => [...prev, newMaterial]);
        } else if (dialogType === 'supplier') {
          const newSupplier: Supplier = {
            id: Date.now().toString(),
            ...supplierForm,
            isActive: true,
            rating: supplierForm.rating || 0,
          };
          setSuppliers(prev => [...prev, newSupplier]);
        }
      }
      
      handleCloseDialog();
    } catch (error) {
      setError('Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type: 'material' | 'supplier', id: string) => {
    if (type === 'material') {
      setMaterials(prev => prev.filter(m => m.id !== id));
    } else {
      setSuppliers(prev => prev.filter(s => s.id !== id));
    }
  };

  const handleStockMovement = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newMovement: StockMovement = {
        id: Date.now().toString(),
        materialId: movementForm.materialId,
        materialName: materials.find(m => m.id === movementForm.materialId)?.name || '',
        type: movementForm.type,
        quantity: movementForm.quantity,
        reason: movementForm.reason,
        date: new Date().toISOString(),
        userId: 'current-user',
        userName: 'Current User',
      };
      
      setStockMovements(prev => [...prev, newMovement]);
      
      // Update material stock
      if (movementForm.type === 'in') {
        setMaterials(prev => prev.map(m => 
          m.id === movementForm.materialId 
            ? { ...m, stockQuantity: m.stockQuantity + movementForm.quantity }
            : m
        ));
      } else if (movementForm.type === 'out') {
        setMaterials(prev => prev.map(m => 
          m.id === movementForm.materialId 
            ? { ...m, stockQuantity: Math.max(0, m.stockQuantity - movementForm.quantity) }
            : m
        ));
      }
      
      handleCloseDialog();
    } catch (error) {
      setError('Failed to record stock movement');
    } finally {
      setLoading(false);
    }
  };

  const handleImportClick = () => setImportDialogOpen(true);
  const handleImportClose = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImportResult(null);
    setImportError(null);
  };
  const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImportFile(e.target.files[0]);
    }
  };
  const handleImportSubmit = async () => {
    if (!importFile) return;
    setImportLoading(true);
    setImportError(null);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const result = await importMaterials(formData).unwrap();
      setImportResult(result);
      // Optionally refresh materials list here
    } catch (err: any) {
      setImportError(err?.data?.message || 'Failed to import materials');
    } finally {
      setImportLoading(false);
    }
  };

  const filteredMaterials = materials.filter(material => {
    const matchesSearch = 
      material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === 'all' || material.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && material.isActive) ||
      (statusFilter === 'inactive' && !material.isActive);

    return matchesSearch && matchesType && matchesStatus;
  });

  const stats = {
    totalMaterials: materials.length,
    lowStock: materials.filter(m => m.stockQuantity <= m.reorderLevel).length,
    outOfStock: materials.filter(m => m.stockQuantity === 0).length,
    totalValue: materials.reduce((sum, m) => sum + (m.stockQuantity * m.unitPrice), 0),
    totalSuppliers: suppliers.length,
    activeSuppliers: suppliers.filter(s => s.isActive).length,
  };

  const getStockStatus = (material: Material) => {
    if (material.stockQuantity === 0) return 'out_of_stock';
    if (material.stockQuantity <= material.reorderLevel) return 'low_stock';
    return 'in_stock';
  };

  const stockStatusMap = {
    in_stock: { label: 'In Stock', color: 'success' },
    low_stock: { label: 'Low Stock', color: 'warning' },
    out_of_stock: { label: 'Out of Stock', color: 'error' },
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h3" 
          gutterBottom 
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              width: 48,
              height: 48,
            }}
          >
            <Inventory />
          </Avatar>
          Inventory Management
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Manage materials, suppliers, and stock movements
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    width: 40,
                    height: 40,
                    mr: 2,
                  }}
                >
                  <Inventory />
                </Avatar>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Total Materials
                </Typography>
              </Box>
              <Typography 
                variant="h4" 
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                {materials.length}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={100} 
                sx={{ 
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 3,
                  },
                }} 
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    width: 40,
                    height: 40,
                    mr: 2,
                  }}
                >
                  <Business />
                </Avatar>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Active Suppliers
                </Typography>
              </Box>
              <Typography 
                variant="h4" 
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                {suppliers.filter(s => s.isActive).length}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={suppliers.length ? (suppliers.filter(s => s.isActive).length / suppliers.length) * 100 : 0} 
                sx={{ 
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(67, 233, 123, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    borderRadius: 3,
                  },
                }} 
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    width: 40,
                    height: 40,
                    mr: 2,
                  }}
                >
                  <Warning />
                </Avatar>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Low Stock Items
                </Typography>
              </Box>
              <Typography 
                variant="h4" 
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                {materials.filter(m => getStockStatus(m) === 'low_stock').length}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={materials.length ? (materials.filter(m => getStockStatus(m) === 'low_stock').length / materials.length) * 100 : 0} 
                sx={{ 
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(250, 112, 154, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    borderRadius: 3,
                  },
                }} 
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    width: 40,
                    height: 40,
                    mr: 2,
                  }}
                >
                  <LocalShipping />
                </Avatar>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Recent Movements
                </Typography>
              </Box>
              <Typography 
                variant="h4" 
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                {stockMovements.length}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={75} 
                sx={{ 
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(79, 172, 254, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    borderRadius: 3,
                  },
                }} 
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 3,
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
          mb: 3,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
            },
          }}
        >
          <Tab label="Materials" />
          <Tab label="Suppliers" />
          <Tab label="Stock Movements" />
        </Tabs>
      </Card>

      {/* Filters */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="fabric">Fabric</MenuItem>
                <MenuItem value="wood">Wood</MenuItem>
                <MenuItem value="metal">Metal</MenuItem>
                <MenuItem value="leather">Leather</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => window.location.reload()}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog('material')}
              disabled={!canManage('manager:inventory') && !canManage('admin:inventory')}
            >
              Add Material
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleImportClick}
              disabled={!canManage('manager:inventory') && !canManage('admin:inventory')}
              sx={{ ml: 1 }}
            >
              Import from Excel/CSV
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Materials Tab */}
      <TabPanel value={tabValue} index={0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Material</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Stock Level</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMaterials.map((material) => {
                const stockStatus = getStockStatus(material);
                return (
                  <TableRow key={material.id}>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {material.id.slice(0, 8)}…
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {material.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {material.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={material.type} size="small" />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {material.stockQuantity} {material.unit}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(material.stockQuantity / material.maxStock) * 100}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        KES {material.unitPrice.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {material.supplierName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={stockStatusMap[stockStatus].label}
                        color={stockStatusMap[stockStatus].color as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Material">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('material', material)}
                            disabled={!canManage('manager:inventory') && !canManage('admin:inventory')}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Stock Movement">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('movement', material)}
                            disabled={!canManage('manager:inventory') && !canManage('admin:inventory')}
                          >
                            <LocalShipping />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Material">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete('material', material.id)}
                            disabled={!canManage('manager:inventory') && !canManage('admin:inventory')}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Suppliers Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 2, textAlign: 'right' }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog('supplier')}
            disabled={!canManage('manager:inventory') && !canManage('admin:inventory')}
          >
            Add Supplier
          </Button>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suppliers.map((supplier) => (
                <TableRow key={supplier.id}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {supplier.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{supplier.email}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {supplier.phone}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {supplier.rating}
                      </Typography>
                      <Typography variant="body2" color="warning.main">
                        ★
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={supplier.isActive ? 'Active' : 'Inactive'}
                      color={supplier.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit Supplier">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('supplier', supplier)}
                          disabled={!canManage('manager:inventory') && !canManage('admin:inventory')}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Supplier">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete('supplier', supplier.id)}
                          disabled={!canManage('manager:inventory') && !canManage('admin:inventory')}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Stock Movements Tab */}
      <TabPanel value={tabValue} index={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Material</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>User</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stockMovements.map((movement) => (
                <TableRow key={movement.id}>
                  <TableCell>{movement.materialName}</TableCell>
                  <TableCell>
                    <Chip
                      label={movement.type.toUpperCase()}
                      color={movement.type === 'in' ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{movement.quantity}</TableCell>
                  <TableCell>{movement.reason}</TableCell>
                  <TableCell>
                    {new Date(movement.date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{movement.userName}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Dialogs */}
      {/* Material Dialog */}
      <Dialog open={openDialog && dialogType === 'material'} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Material' : 'Add New Material'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={materialForm.name}
                onChange={(e) => setMaterialForm({ ...materialForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={materialForm.type}
                  onChange={(e) => setMaterialForm({ ...materialForm, type: e.target.value })}
                  label="Type"
                  required
                >
                  <MenuItem value="fabric">Fabric</MenuItem>
                  <MenuItem value="wood">Wood</MenuItem>
                  <MenuItem value="metal">Metal</MenuItem>
                  <MenuItem value="leather">Leather</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={materialForm.description}
                onChange={(e) => setMaterialForm({ ...materialForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit"
                value={materialForm.unit}
                onChange={(e) => setMaterialForm({ ...materialForm, unit: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                value={materialForm.unitPrice}
                onChange={(e) => setMaterialForm({ ...materialForm, unitPrice: parseFloat(e.target.value) })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stock Quantity"
                type="number"
                value={materialForm.stockQuantity}
                onChange={(e) => setMaterialForm({ ...materialForm, stockQuantity: parseInt(e.target.value) })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reorder Level"
                type="number"
                value={materialForm.reorderLevel}
                onChange={(e) => setMaterialForm({ ...materialForm, reorderLevel: parseInt(e.target.value) })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Min Stock"
                type="number"
                value={materialForm.minStock}
                onChange={(e) => setMaterialForm({ ...materialForm, minStock: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Stock"
                type="number"
                value={materialForm.maxStock}
                onChange={(e) => setMaterialForm({ ...materialForm, maxStock: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={materialForm.supplierId}
                  onChange={(e) => setMaterialForm({ ...materialForm, supplierId: e.target.value })}
                  label="Supplier"
                >
                  {suppliers.map(supplier => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={materialForm.location}
                onChange={(e) => setMaterialForm({ ...materialForm, location: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Dialog */}
      <Dialog open={openDialog && dialogType === 'supplier'} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Supplier' : 'Add New Supplier'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={supplierForm.name}
                onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={supplierForm.email}
                onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={supplierForm.phone}
                onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={supplierForm.address}
                onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rating"
                type="number"
                inputProps={{ min: 0, max: 5, step: 0.1 }}
                value={supplierForm.rating}
                onChange={(e) => setSupplierForm({ ...supplierForm, rating: parseFloat(e.target.value) || 0 })}
                helperText="Rating from 0 to 5"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stock Movement Dialog */}
      <Dialog open={openDialog && dialogType === 'movement'} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Stock Movement</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Material</InputLabel>
                <Select
                  value={movementForm.materialId}
                  onChange={(e) => setMovementForm({ ...movementForm, materialId: e.target.value })}
                  label="Material"
                  required
                >
                  {materials.map(material => (
                    <MenuItem key={material.id} value={material.id}>
                      {material.name} ({material.stockQuantity} {material.unit})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={movementForm.type}
                  onChange={(e) => setMovementForm({ ...movementForm, type: e.target.value as any })}
                  label="Type"
                  required
                >
                  <MenuItem value="in">Stock In</MenuItem>
                  <MenuItem value="out">Stock Out</MenuItem>
                  <MenuItem value="adjustment">Adjustment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={movementForm.quantity}
                onChange={(e) => setMovementForm({ ...movementForm, quantity: parseInt(e.target.value) })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={2}
                value={movementForm.reason}
                onChange={(e) => setMovementForm({ ...movementForm, reason: e.target.value })}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleStockMovement}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Process Movement'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={handleImportClose} maxWidth="sm" fullWidth>
        <DialogTitle>Import Materials from Excel/CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleImportFileChange}
              disabled={importLoading}
            />
          </Box>
          {importLoading && <CircularProgress sx={{ mb: 2 }} />}
          {importResult && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Imported: {importResult.imported} materials.<br />
              {importResult.errors && importResult.errors.length > 0 && (
                <>
                  {importResult.errors.length} errors:<br />
                  <ul style={{ maxHeight: 100, overflow: 'auto' }}>
                    {importResult.errors.map((err: any, idx: number) => (
                      <li key={idx}>{err.error} (Row: {JSON.stringify(err.row)})</li>
                    ))}
                  </ul>
                </>
              )}
            </Alert>
          )}
          {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}
          <Alert severity="info">
            Please ensure your Excel/CSV columns are: Name, Description, Type, Unit, Stock Quantity, Reorder Level, Unit Price, Supplier, Location, Min Stock, Max Stock.<br />
            Duplicate materials (by name) will be skipped unless you upload a new batch with unique names.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportClose}>Cancel</Button>
          <Button
            onClick={handleImportSubmit}
            variant="contained"
            disabled={!importFile || importLoading}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add material"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleOpenDialog('material')}
        disabled={!canManage('manager:inventory') && !canManage('admin:inventory')}
      >
        <Add />
      </Fab>
    </Box>
  );
};
